import { logger } from "#webapp/src/util/logger.util";
import { ApolloError } from "@apollo/client";
import {
  all as allTimeSlots,
  timeSlotsCeiling,
  timeSlotsFloor,
} from "@clockwise/client-commons/src/constants/time-slot";
import {
  RecommendedSlotRange,
  getAutopilotTimeOfDayFlexibilitySummaryText,
} from "@clockwise/client-commons/src/util/time-slot";
import { AutopilotEventStatusEnum } from "@clockwise/schema";
import {
  DayOfWeek,
  FlexRange,
  ProposalAppliesTo,
  ProposalState,
  TradeoffType,
} from "@clockwise/schema/v2";
import {
  useGatewayMutation,
  useGatewayQuery,
} from "@clockwise/web-commons/src/network/apollo/gateway-provider";
import { useUpdateActiveEvent } from "@clockwise/web-commons/src/util/ActiveEventContext";
import { TrackingEvents, useTracking } from "@clockwise/web-commons/src/util/analytics.util";
import { compact } from "lodash";
import { Interval } from "luxon";
import React, { useEffect, useRef, useState } from "react";
import {
  useCurrentProposal,
  useUpdateCurrentProposal,
} from "../../chat-plus-calendar/CurrentProposalContext";
import {
  usePersistedProposal,
  useUpdatePersistedProposal,
} from "../../chat-plus-calendar/PersistedProposalContext";
import { useUpdateHoverEvent } from "../../chat-plus-calendar/util/HoverEventContext";
import { ConfirmProposalDocument } from "../../reschedule-confirmation-modal/__generated__/ConfirmProposal.v2.generated";
import { TradeoffBlocks } from "../../tradeoffs/TradeoffBlocks";
import { CalendarEventsDocument } from "../../web-app-calendar/apollo/__generated__/CalendarEvents.v2.generated";
import { eventToast } from "../../web-app-calendar/notifiation-event/EventToast";
import { CardWrapper } from "../CardWrapper";
import { CollapsibleSection } from "../atoms/CollapsibleSection";
import { Divider } from "../atoms/Divider";
import { EventAttendeesProvider } from "../hooks/EventAttendeesContext";
import {
  EventFlexConstraintsProvider,
  getRangeDescription,
} from "../hooks/EventFlexConstraintsContext";
import { CreateEventMutation } from "../hooks/__generated__/CreateEvent.v2.generated";
import { useCreateEvent } from "../hooks/useCreateEvent";
import { ECAgenda } from "../molecules/ECAgenda";
import { FlexibilityPanel } from "../molecules/ECFlexibility";
import { ECLocation } from "../molecules/ECLocation";
import { ECRecurrence } from "../molecules/ECRecurrence";
import { ECTitle } from "../molecules/ECTitle";
import { ALL_WORK_DAYS_SELECTED } from "../types";
import { AttendeePanel } from "./AttendeePanel";
import { ConferenceTypeSelector } from "./ConferenceTypeSelector";
import { Footer } from "./Footer";
import { TimeIntervalSelector } from "./TimeIntervalSelector";
import { CurrentProposalMembersDocument } from "./__generated__/CurrentProposalMembers.v2.generated";
import { toEventCardAttendee } from "./utils/attendee";

const FULL_DAY_SLOT_RANGE: RecommendedSlotRange = [0, 47];

function getFlexDescription(
  start?: string | null | undefined,
  end?: string | null | undefined,
  flexRange?: FlexRange | null | undefined,
  allowedDays?: DayOfWeek[] | null | undefined,
) {
  const allowedStartSlot = start ? timeSlotsFloor.indexOf(start) : 0;
  const allowedEndSlot = end ? timeSlotsCeiling.indexOf(end) : timeSlotsCeiling.length - 1;
  const textSummary = getAutopilotTimeOfDayFlexibilitySummaryText(
    allTimeSlots[allowedStartSlot],
    allTimeSlots[allowedEndSlot],
    allowedStartSlot === 0,
    allowedEndSlot === timeSlotsCeiling.length - 1,
  );

  const rangeDescription = getRangeDescription(flexRange, allowedDays);
  if (rangeDescription) {
    return `${rangeDescription} ⸱ ${textSummary}`;
  }
  return textSummary;
}

const ProposalFooter = ({ disabled }: { disabled?: boolean }) => {
  const updateActiveEvent = useUpdateActiveEvent();
  const { clearProposal } = useUpdateCurrentProposal();
  const { clearPersistedProposal } = useUpdatePersistedProposal();

  const { onCreateEvent, loading: saving } = useCreateEvent();
  const { currentProposal } = useCurrentProposal();
  const { id: proposalId, tradeoffBlocks } = usePersistedProposal();
  const track = useTracking();
  const [confirmProposal, { loading: confirming }] = useGatewayMutation(ConfirmProposalDocument, {
    refetchQueries: [CalendarEventsDocument],
    awaitRefetchQueries: true,
  });

  const clearCurrentAndPersistedProposal = () => {
    clearProposal();
    clearPersistedProposal();
  };

  const onEventCreatedSuccess = (data: CreateEventMutation) => {
    if (!data.createEvent) {
      clearCurrentAndPersistedProposal();
      return;
    }

    track(TrackingEvents.DIRECT_MANIPULATION.NEW_EVENT_CARD.SAVE_BUTTON.CLICKED, {
      eventId: data?.createEvent?.id,
      proposalId,
      timeDescription: data?.createEvent?.timeDescription,
    });
    clearCurrentAndPersistedProposal();
    eventToast.success({
      title: data.createEvent.title,
      operation: "ADD",
    });
  };

  const onEventCreatedError = (error: ApolloError, title: string) => {
    clearCurrentAndPersistedProposal();
    eventToast.error({
      title,
      operation: "ADD",
    });
    logger.error(`Failed to create event: ${error.message}`, error);
  };

  const handleSave = async () => {
    if (!currentProposal) return;
    const {
      title,
      startTime,
      endTime,
      timeZone,
      description,
      attendees,
      recurrenceRule,
      location,
      conferenceType,
      flexDetails,
    } = currentProposal;

    // Immediately close the event card and show a loading toast.
    updateActiveEvent(null);

    await onCreateEvent({
      title,
      description,
      timeRange: Interval.fromDateTimes(startTime, endTime).toISO(),
      timeZone,
      attendees: attendees.map((a) => ({
        email: a.person.email,
        optional: a.isOptional,
      })),
      recurrenceRule,
      location,
      conferenceType,
      flexDetails,
      callback: onEventCreatedSuccess,
      errorCallback: (error) => onEventCreatedError(error, title),
    });
  };

  const handleSaveAndFix = async () => {
    if (!currentProposal || !proposalId) return;

    try {
      const result = await confirmProposal({
        variables: {
          proposalId,
          appliesTo: ProposalAppliesTo.AllInstances,
        },
      });

      if (result.data?.confirmProposal) {
        const confirmedProposal = result.data.confirmProposal;
        const diff = confirmedProposal.diffBlocks[0].diffs[0];
        const addDiff = diff.__typename === "AddDiff" ? diff : null;
        const timeRange =
          addDiff?.time?.__typename === "DateRange"
            ? addDiff.time.dateRange
            : addDiff?.time?.timeRange;
        const clientTimeRange = Interval.fromDateTimes(
          currentProposal.startTime,
          currentProposal.endTime,
        );
        // NB: (lsanwick) Add a log to track if the proposal drifted from the current client time range.
        if (
          timeRange &&
          !Interval.fromISO(timeRange, { zone: currentProposal.timeZone }).equals(clientTimeRange)
        ) {
          logger.error("Proposal differs from current proposal", {
            proposalId,
            persistedTimeRange: Interval.fromISO(timeRange, {
              zone: currentProposal.timeZone,
            }).toISO(),
            currentTimeRange: clientTimeRange.toISO(),
          });
        }
        clearCurrentAndPersistedProposal();
        updateActiveEvent(null);
        eventToast.success({
          title: currentProposal.title,
          operation: "ADD",
        });
        track(TrackingEvents.DIRECT_MANIPULATION.NEW_EVENT_CARD.SAVE_AND_FIX_BUTTON.CLICKED, {
          proposalId,
        });
      } else {
        throw new Error("Failed to confirm proposal");
      }
    } catch (error) {
      eventToast.error({
        title: currentProposal.title,
        operation: "ADD",
      });
      logger.error(`Failed to confirm proposal: ${error.message}`, error);
    }
  };

  // Dev Note: only FixableConflict tradeoffs with diffId are considered active
  // And this number reduces if user removes a tradeoff.
  // The diffId is removed when user removes a tradeoff
  const numberOfActiveTradeoffs =
    tradeoffBlocks
      ?.filter((block) => block.tradeoffType === TradeoffType.FixableConflict)
      .flatMap((block) => block.tradeoffs)
      .filter((tradeoff) => tradeoff.diffId).length ?? 0;

  return (
    <Footer
      disabled={disabled || saving || confirming || currentProposal.attendees.length === 0}
      conflictCount={numberOfActiveTradeoffs}
      onSave={handleSave}
      onSaveAndFix={handleSaveAndFix}
    />
  );
};

export const ProposalCardManager = () => {
  const titleRef = useRef<HTMLInputElement>(null);
  const track = useTracking();
  const [error, setError] = useState<string | null>(null);
  const { currentProposal } = useCurrentProposal();
  const { status: proposalStatus, tradeoffBlocks } = usePersistedProposal();
  const updateActiveEvent = useUpdateActiveEvent();
  const updateHoverEvent = useUpdateHoverEvent();

  const {
    updateTime,
    updateDescription,
    updateAllDay,
    updateTitle,
    addAttendee,
    removeAttendee,
    setAttendees,
    updateAttendeeOptionality,
    updateRecurrenceRule,
    updateConferenceType,
    updateLocation,
    clearProposal,
    updateFlexDetails,
  } = useUpdateCurrentProposal();
  const { clearPersistedProposal } = useUpdatePersistedProposal();

  const {
    title,
    description,
    location,
    startTime,
    endTime,
    allDay,
    attendees,
    conferenceType,
    flexDetails,
    initialAttendeeIds,
  } = currentProposal;

  const { loading: loadingMembers } = useGatewayQuery(CurrentProposalMembersDocument, {
    variables: {
      calendarIds: initialAttendeeIds,
    },
    skip: !currentProposal.initialized,
    onCompleted(data) {
      const displayAttendees = compact(data?.members).map((m) => ({
        isOptional: false,
        ...m,
      }));
      setAttendees(displayAttendees);
    },
  });

  useEffect(() => {
    // Focus the title input when the component mounts
    setTimeout(() => {
      titleRef.current?.focus();
      titleRef.current?.select();
    }, 250);
  }, []);

  if (!currentProposal?.initialized) {
    return <></>;
  }

  const handleClose = () => {
    updateActiveEvent(null);
    updateHoverEvent(null);
    clearProposal();
    clearPersistedProposal();
  };

  return (
    <CardWrapper
      onClose={handleClose}
      header={<div className="cw-font-semibold">New event</div>}
      footer={<ProposalFooter disabled={!!error || currentProposal.attendees.length === 0} />}
    >
      <ECTitle
        eventName={title}
        readOnly={false}
        onEventNameChange={(e) => {
          updateTitle(e.target.value);
        }}
        ref={titleRef}
      />
      <AttendeePanel
        attendees={attendees}
        loading={loadingMembers}
        readOnly={false}
        onAddAttendee={addAttendee}
        onRemoveAttendee={removeAttendee}
        onUpdateAttendeeOptionality={updateAttendeeOptionality}
      />
      <Divider />
      <EventAttendeesProvider attendees={attendees.map(toEventCardAttendee)}>
        <EventFlexConstraintsProvider
          startTime={startTime}
          eventId={null}
          calendarId={initialAttendeeIds[0]}
        >
          <FlexibilityPanel
            onFlexibilityChange={(isFlexible) => updateFlexDetails({ ...flexDetails, isFlexible })}
            onFlexDetailsChange={updateFlexDetails}
            getFlexDescription={getFlexDescription}
            readOnly={false}
            showChanges={false}
            flexStatus={flexDetails.isFlexible ? AutopilotEventStatusEnum.CanMove : null}
            flexDetails={flexDetails}
            availableWorkDays={ALL_WORK_DAYS_SELECTED}
            minMaxRecommendedValues={FULL_DAY_SLOT_RANGE}
            usersWorkingHours={[]} // Rendering without working hours for now.
          />
        </EventFlexConstraintsProvider>
      </EventAttendeesProvider>
      <Divider />
      <TimeIntervalSelector
        startTime={startTime}
        endTime={endTime}
        isAllDay={allDay}
        readOnly={false}
        onChangeTime={(newStart, newEnd) => {
          if (!newStart || !newEnd) return;
          track(TrackingEvents.DIRECT_MANIPULATION.NEW_EVENT_CARD.UPDATED_TIME, {
            startTime: newStart,
            endTime: newEnd,
          });
          updateTime(newStart, newEnd);
        }}
        onChangeAllDay={updateAllDay}
        onError={setError}
      />
      <ECRecurrence
        recurrenceRule={currentProposal.recurrenceRule}
        date={startTime.toISODate()}
        onSelect={updateRecurrenceRule}
        readOnly={false}
        originalRecurrence={null}
      />
      {proposalStatus && (
        <div className="cw-mx-3 cw-border-solid cw-border cw-border-subtle cw-p-2 cw-rounded-lg">
          <TradeoffBlocks
            blocks={tradeoffBlocks ?? []}
            loading={proposalStatus === ProposalState.LoadingSuggestions}
          />
        </div>
      )}
      <Divider />
      <CollapsibleSection numberOfSections={3} defaultExpand>
        <ConferenceTypeSelector selected={conferenceType} onSelect={updateConferenceType} />
        <ECLocation
          location={location ?? ""}
          onLocationChange={(location) => {
            updateLocation(location ?? "");
          }}
        />
        <ECAgenda
          agenda={description}
          onChangeAgenda={(updatedDescription) => {
            updateDescription(updatedDescription);
          }}
          readOnly={false}
        />
      </CollapsibleSection>
    </CardWrapper>
  );
};
